import React, { useState, useMemo, useEffect } from "react";
import { useQuery, useApolloClient } from "@apollo/client";
import {
  Row,
  Col,
  Typography,
  Table,
  Button,
  Input,
  Form,
  Space,
  Card,
  Tag,
  Tooltip,
} from "antd";
import {
  FileExcelFilled,
  SearchOutlined,
  CheckCircleOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { format, startOfMonth, endOfDay } from "date-fns";
import { Link } from "react-router-dom";
import withClinic from "../../hooks/with_clinic";
import DateRange from "../../components/date_range";
import HorizontalScrollContainer from "../../components/horizontal_scroll_container";
import { Clinic, ApolloResult } from "../../store";
import paths from "../../routes/paths";
import {
  get_credit_received_list,
  getCreditReceivedListVar,
  CreditReceivedListItem,
  CreditReceivedListResponse,
} from "../../graphql/reports/credit_received";
import Error from "../../components/apollo_error";
import { formatMoney, getCurrency, formatPhoneNumber } from "../../helpers/utils";
import * as XLSX from "xlsx";
import FileSaver from "file-saver";

const { Title, Text } = Typography;

interface Props {
  currentClinic?: Clinic;
}

interface TableRecord extends CreditReceivedListItem {
  key: string;
}

const CreditReceivedReportPage: React.FC<Props> = (props) => {
  const clinicId = props.currentClinic?.id || "";
  const currency = getCurrency(props.currentClinic);
  const client = useApolloClient();

  const [form] = Form.useForm();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [pageSize, setPageSize] = useState<number>(20);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [exporting, setExporting] = useState<boolean>(false);
  const [ranges, setRanges] = useState({
    startDate: startOfMonth(new Date()),
    endDate: endOfDay(new Date()),
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [ranges.startDate, ranges.endDate, searchTerm, pageSize]);

  const skip = (currentPage - 1) * pageSize;

  const variables = getCreditReceivedListVar({
    clinic_id: clinicId,
    start_date: ranges.startDate,
    end_date: ranges.endDate,
    search: searchTerm,
    skip: skip,
    take: pageSize,
  });

  const { loading, data, error, refetch } = useQuery<
    ApolloResult<"getCreditReceivedList", CreditReceivedListResponse>
  >(get_credit_received_list, {
    variables,
    skip: !clinicId,
    fetchPolicy: "cache-and-network",
  });

  const responseData = data?.getCreditReceivedList;
  const listItems = responseData?.data || [];
  const totalCount = responseData?.totalCount || 0;

  const handleDateChange = (startDate: Date, endDate: Date) => {
    setRanges({ startDate, endDate });
  };

  const handleExportExcel = async () => {
    if (!clinicId) return;
    try {
      setExporting(true);

      const exportVariables = getCreditReceivedListVar({
        clinic_id: clinicId,
        start_date: ranges.startDate,
        end_date: ranges.endDate,
        search: searchTerm,
        skip: 0,
        take: 10000,
      });

      const { data: exportData } = await client.query<
        ApolloResult<"getCreditReceivedList", CreditReceivedListResponse>
      >({
        query: get_credit_received_list,
        variables: exportVariables,
        fetchPolicy: "network-only",
      });

      const exportList = exportData?.getCreditReceivedList?.data || [];

      // Same column names and rendered values as the on-screen table
      const rows = exportList.map((item) => ({
        "Payment Date": item.payment_date
          ? format(new Date(item.payment_date), "yyyy-MM-dd HH:mm")
          : "-",
        "OrderNo": item.invoice_id || item.order_id || "-",
        "Member": item.member_name || "-",
        "Sold By": item.user_name || "-",
        "Payment": item.payment_method || "-",
        "Payment Amount": item.payment_amount ?? 0,
        "Type": item.is_increasement ? "Increment" : "Standard",
        "Note": item.payment_note || "-",
      }));

      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Credit Received");

      const dateStr = format(new Date(), "yyyyMMdd_HHmmss");
      const fileName = `Credit_Received_Report_${dateStr}.xlsx`;

      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      const fileData = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
      });

      FileSaver.saveAs(fileData, fileName);
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setExporting(false);
    }
  };

  const columns = [
    {
      title: "Payment Date",
      dataIndex: "payment_date",
      key: "payment_date",
      width: 150,
      render: (val: Date | string) =>
        val ? format(new Date(val), "yyyy-MM-dd HH:mm") : "-",
      sorter: (a: TableRecord, b: TableRecord) =>
        new Date(a.payment_date).getTime() - new Date(b.payment_date).getTime(),
    },
    {
      title: "OrderNo",
      dataIndex: "invoice_id",
      key: "invoice_id",
      width: 140,
      render: (val: string, record: TableRecord) =>
        record.order_id ? (
          <Link to={paths.getSaleDetailRoute(record.order_id)}>
            {val || record.order_id}
          </Link>
        ) : (
          <Text strong>{val || "-"}</Text>
        ),
    },
    {
      title: "Member",
      key: "member",
      width: 220,
      render: (record: TableRecord) => (
        <div>
          <div>
            <Link to={paths.getMemberDetailRoute(record.member_id)}>
              {record.member_name}
            </Link>
          </div>
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {formatPhoneNumber(record.member_phone, props.currentClinic)}
            </Text>
            {record.member_code && (
              <Tag style={{ marginLeft: 6, fontSize: 10 }}>{record.member_code}</Tag>
            )}
          </div>
        </div>
      ),
    },
    {
      title: "Sold By",
      dataIndex: "user_name",
      key: "user_name",
      width: 140,
      render: (val: string) => (
        <Space size={4}>
          <Text>{val || "-"}</Text>
        </Space>
      ),
    },
    {
      title: "Payment",
      dataIndex: "payment_method",
      key: "payment_method",
      width: 140,
    },
    {
      title: "Payment Amount",
      dataIndex: "payment_amount",
      key: "payment_amount",
      width: 160,
      align: "right" as const,
      render: (val: number) => (
        <Text style={{ fontSize: 15 }}>
          {formatMoney(val)} {currency}
        </Text>
      ),
      sorter: (a: TableRecord, b: TableRecord) =>
        a.payment_amount - b.payment_amount,
    },
    {
      title: "Type",
      dataIndex: "is_increasement",
      key: "is_increasement",
      width: 110,
      render: (val: boolean) => <Tag>{val ? "Increment" : "Standard"}</Tag>,
    },
    {
      title: "Note",
      key: "info_note",
      width: 200,
      render: (record: TableRecord) => (
        <div>
          {record.payment_note && (
            <div>
              <Text style={{ fontSize: 12 }}>
                {record.payment_note}
              </Text>
            </div>
          )}
          {!record.payment_info && !record.payment_note && "-"}
        </div>
      ),
    },
  ];

  if (error) {
    return <Error error={error} />;
  }

  const tableData: TableRecord[] = listItems.map((item, idx) => ({
    ...item,
    key: item.payment_id || `item-${idx}`,
  }));

  const control = (
    <Row style={{ marginTop: -18, marginBottom: 12 }} align="middle" justify="space-between">
      <Col flex={4}>
        <Title level={4} style={{ margin: 0 }}>
          Credit Received Report
        </Title>
      </Col>
      <Col flex={0}>
        <Space size={8}>
          <Input
            placeholder="Search..."
            prefix={<SearchOutlined />}
            value={searchTerm}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
            style={{ width: 200 }}
            allowClear
          />
          <DateRange
            startDate={ranges.startDate}
            endDate={ranges.endDate}
            onSelect={handleDateChange}
          />
          <Button
            loading={exporting}
            icon={<FileExcelFilled />}
            onClick={handleExportExcel}
          >
            Export
          </Button>
        </Space>
      </Col>
    </Row>
  );

  return (
    <React.Fragment>
      {control}

      <Row style={{ marginTop: 8 }}>
        <Col span={24}>
            <Form form={form} component={false}>
              <HorizontalScrollContainer>
                <Table<TableRecord>
                  columns={columns}
                  dataSource={tableData}
                  loading={loading}
                  scroll={{ x: 1400 }}
                  pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    total: totalCount,
                    showSizeChanger: true,
                    pageSizeOptions: ["10", "20", "30", "50", "100"],
                    onChange: (page: number, size?: number) => {
                      setCurrentPage(page);
                      if (size && size !== pageSize) {
                        setPageSize(size);
                      }
                    },
                    showTotal: (total: number, range: [number, number]) =>
                      `${range[0]}-${range[1]} of ${total} payments`,
                  }}
                />
              </HorizontalScrollContainer>
            </Form>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default withClinic(CreditReceivedReportPage);